import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useDevOverride,
  type CurrentPeriod,
} from "../../context/DevOverrideContext";
import { useSchoolPasses } from "../../hooks/useSchoolPasses";
import { useClassSections, type ClassSection } from "../../hooks/useClassSections";
import { useLoadingStates } from "../../hooks/useLoadingStates";
import {
  PASS_STATUS,
  listDestinations,
  cancelPass as cancelPassApi,
  returnPass,
} from "../../api/studentPassesApi";
import {
  approvePass as approvePassApi,
  denyPass as denyPassApi,
} from "../../api/teacherPassesApi";
import {
  getScheduleDisplayName,
  ScheduleConfigurationError,
  type ABDay,
} from "../../utils/scheduleUtils";
import { showError, showSuccess, showInfo } from "../../utils/notifications";
import {
  getContextualError,
  logError,
  PASS_ALREADY_UPDATED_MESSAGE,
} from "../../utils/errorHandling";
import { periodsEqual } from "../../utils/periodFormatHelper";
import PassHistoryView from "../admin/PassHistoryView";
import TeacherPassSubmission from "./TeacherPassSubmission";
import ScheduleErrorScreen from "../common/ScheduleErrorScreen";
import LoadingScreen from "../common/LoadingScreen";
import ConnectionStatusBanner from "../common/ConnectionStatusBanner";
import type { components } from "../../api/types.gen";
import type { Pass } from "../../hooks/usePassSocket";
import styles from "./TeacherDashboard.module.css";

type DestinationResponse = components["schemas"]["DestinationResponse"];

// Minimal shape narrowed from a caught error to read its HTTP status.
interface ApiErrorLike {
  status?: number;
}

const TeacherDashboard = (): React.ReactElement => {
  const { currentUser, logout } = useAuth();
  // TODO(slice:foundation): the old multi-org context is gone (multi-org
  // orgs have no hallpass-backend equivalent). schoolId is the closest
  // stand-in for the utils below (scheduleUtils, AnnouncementBanner,
  // PassHistoryView, TeacherPassSubmission) that still expect an org
  // identifier and haven't been migrated off it yet.
  const schoolId = currentUser?.schoolId;
  const {
    getCurrentPeriod,
    getScheduleType,
    getABDay,
    isAutoMode,
    isDeveloperMode,
    updateTrigger,
    scheduleVariantsMap,
  } = useDevOverride();

  const { passes, connectionStatus, refresh, getPassesForClassSection } =
    useSchoolPasses();
  const { classSections, loading: classSectionsLoading } = useClassSections();

  const [destinations, setDestinations] = useState<DestinationResponse[]>([]);
  const [destinationsError, setDestinationsError] = useState<string | null>(
    null,
  );
  const [showPassSubmission, setShowPassSubmission] = useState(false);
  const [selectedClassSectionId, setSelectedClassSectionId] = useState("");
  const [hasUserSelectedSection, setHasUserSelectedSection] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [hasUserSelectedPeriod, setHasUserSelectedPeriod] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState<CurrentPeriod | null>(
    null,
  );
  const [scheduleType, setScheduleType] = useState<string | null>(null);
  const [abDay, setABDay] = useState<ABDay | string | null>("A");

  // Tick state for updating over-limit calculations
  const [, setTick] = useState(0);

  // Schedule error state
  const [scheduleError, setScheduleError] = useState<Error | null>(null);

  // Initial loading state
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Loading states for async operations
  const [loadingStates, setLoading] = useLoadingStates({
    denyPass: false,
    markReturned: false,
    cancelPass: false,
  });

  // Approve is tracked per-pass (by id) rather than as a single shared
  // boolean: multiple pending cards render their own Approve button, and a
  // shared flag would disable/relabel every card while any one approval is
  // in flight. Multiple approvals can be in flight at once, each tracked
  // independently by its pass id in this Set, so one approval completing
  // does not clear another's "Approving..." indicator (empty when idle).
  const [approvingPassIds, setApprovingPassIds] = useState<Set<number>>(
    () => new Set(),
  );

  // Distinct periodName values present in this school's class sections,
  // used to populate the period dropdown (mirrors ActivePassesPanel.js's
  // periodNameByClassSectionId pattern, which resolves periods from the
  // same field).
  const periodOptions = useMemo(() => {
    const names = new Set<string>();
    classSections.forEach((cs) => {
      if (cs.periodName) names.add(cs.periodName);
    });
    return Array.from(names).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
  }, [classSections]);

  // Resolve a display name for a pass's classSectionId from the
  // useClassSections data (replaces the old per-pass classroom-name field).
  const classSectionById = useMemo(() => {
    const map = new Map<number, ClassSection>();
    classSections.forEach((cs) => map.set(cs.id, cs));
    return map;
  }, [classSections]);

  const getClassSectionName = (classSectionId: number | null): string => {
    if (classSectionId == null) return "Unassigned";
    const section = classSectionById.get(classSectionId);
    return section ? `${section.periodName} - ${section.name}` : "Unknown";
  };

  // Helper function to check if a pass has been out over 10 minutes
  const isOverLimit = (pass: Pass): boolean => {
    if (!pass.activatedAt) return false;
    const activatedTime = new Date(pass.activatedAt);
    const now = new Date();
    const minutesOut = (now.getTime() - activatedTime.getTime()) / (1000 * 60);
    return minutesOut > 10;
  };

  // Update current period from DevOverrideContext's backend-driven state.
  useEffect(() => {
    const updateCurrentInfo = () => {
      try {
        const schedule = getScheduleType();
        const period = getCurrentPeriod();
        const currentABDay = getABDay();

        setScheduleType(schedule);
        setCurrentPeriod(period);
        setABDay(currentABDay);
        setScheduleError(null); // Clear any previous errors

        // Set intelligent default for the period filter, resolving the
        // backend-derived period number/name against the school's actual
        // (possibly custom) periodName values.
        if (
          !hasUserSelectedPeriod &&
          period &&
          period.periodNumber !== "Advisory" &&
          period.periodNumber !== "Lunch"
        ) {
          const matchedSection = classSections.find((cs) =>
            periodsEqual(period.periodNumber, cs.periodName),
          );
          if (matchedSection) {
            setSelectedPeriod(matchedSection.periodName);
          }
        }
      } catch (error) {
        if (error instanceof ScheduleConfigurationError) {
          console.error(
            "TeacherDashboard: Schedule configuration error:",
            error.message,
          );
          setScheduleError(error);
          setCurrentPeriod(null);
          setScheduleType(null);
        } else {
          console.error(
            "TeacherDashboard: Unexpected error in period update:",
            error,
          );
          // Don't show schedule error for other types of errors
        }
      }
    };

    // Initial update only - backend period detection handles the rest
    updateCurrentInfo();
    // No polling interval - DevOverrideContext listens to backend period state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getCurrentPeriod,
    getScheduleType,
    getABDay,
    updateTrigger,
    classSections,
    hasUserSelectedPeriod,
  ]);

  // Fetch destinations once on mount, for the "Create Pass for Student"
  // modal's destination picker. Unlike the old real-time listener, this is
  // a one-time fetch - an admin-side change won't show up here until reload.
  //
  // Guards against a stale response clobbering `destinations` for the wrong
  // schoolId: if schoolId changes while a fetch is in flight, the cleanup
  // below flips `cancelled` so an earlier request that resolves after a
  // later one is dropped instead of overwriting state. Mirrors the
  // cancelled-flag pattern in AdminPanel.js's checkSchoolSetupStatus effect.
  //
  // Surfaces a fetch failure (visible error + Retry, mirroring
  // StudentDashboard's fetchDestinations) instead of only logging it: an
  // unhandled failure here left the Create-Pass modal's destination dropdown
  // empty and its submit permanently disabled, with no indication why.
  const fetchDestinations = useCallback(
    (isCancelled: () => boolean = () => false) => {
      if (!schoolId) return;
      if (!isCancelled()) setDestinationsError(null);
      listDestinations(schoolId)
        .then(({ data }) => {
          if (!isCancelled()) {
            setDestinations(
              [...data].sort((a, b) => a.name.localeCompare(b.name)),
            );
          }
        })
        .catch((error) => {
          logError(error, "fetchDestinations");
          if (!isCancelled()) {
            setDestinationsError(getContextualError(error, "teacher"));
          }
        });
    },
    [schoolId],
  );

  useEffect(() => {
    let cancelled = false;
    fetchDestinations(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchDestinations]);

  // Set initial loading to false once we have basic data
  useEffect(() => {
    if (currentUser && schoolId) {
      // Give a brief moment for UI to initialize
      const timer = setTimeout(() => {
        setIsInitialLoading(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [currentUser, schoolId]);

  // Pre-select the current teacher's own class section(s) on load (replaces
  // the old last-name-vs-destination-subcategory matching, which has no
  // backend equivalent). Falls back to the unfiltered "All" view if no
  // section belongs to this teacher.
  useEffect(() => {
    if (
      hasUserSelectedSection ||
      selectedClassSectionId !== "" ||
      classSectionsLoading
    ) {
      return;
    }

    const ownSection = classSections.find(
      (cs) => cs.teacherId === currentUser?.id,
    );
    if (ownSection) {
      setSelectedClassSectionId(String(ownSection.id));
    }
  }, [
    classSections,
    classSectionsLoading,
    hasUserSelectedSection,
    selectedClassSectionId,
    currentUser,
  ]);

  // Update tick every 30 seconds to recalculate over-limit passes
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const approvePass = async (passId: number) => {
    setApprovingPassIds((prev) => {
      const next = new Set(prev);
      next.add(passId);
      return next;
    });
    try {
      await approvePassApi(passId);
      showSuccess("Pass approved successfully");
      await refresh();
    } catch (error) {
      if ((error as ApiErrorLike).status === 409) {
        showInfo(PASS_ALREADY_UPDATED_MESSAGE);
        await refresh();
      } else {
        logError(error, "approvePass");
        showError(getContextualError(error, "teacher"));
      }
    } finally {
      setApprovingPassIds((prev) => {
        const next = new Set(prev);
        next.delete(passId);
        return next;
      });
    }
  };

  const denyPass = async (passId: number) => {
    setLoading("denyPass", true);
    try {
      await denyPassApi(passId);
      showSuccess("Pass denied");
      await refresh();
    } catch (error) {
      if ((error as ApiErrorLike).status === 409) {
        showInfo(PASS_ALREADY_UPDATED_MESSAGE);
        await refresh();
      } else {
        logError(error, "denyPass");
        showError(getContextualError(error, "teacher"));
      }
    } finally {
      setLoading("denyPass", false);
    }
  };

  const cancelPass = async (passId: number) => {
    if (!window.confirm("Cancel this pass? This action cannot be undone.")) {
      return;
    }

    setLoading("cancelPass", true);
    try {
      await cancelPassApi(passId);
      showSuccess("Pass canceled successfully");
      await refresh();
    } catch (error) {
      if ((error as ApiErrorLike).status === 409) {
        showInfo(PASS_ALREADY_UPDATED_MESSAGE);
        await refresh();
      } else {
        logError(error, "cancelPass");
        showError(getContextualError(error, "teacher"));
      }
    } finally {
      setLoading("cancelPass", false);
    }
  };

  const markReturned = async (passId: number) => {
    setLoading("markReturned", true);
    try {
      await returnPass(passId);
      showSuccess("Student marked as returned");
      await refresh();
    } catch (error) {
      if ((error as ApiErrorLike).status === 409) {
        showInfo(PASS_ALREADY_UPDATED_MESSAGE);
        await refresh();
      } else {
        logError(error, "markReturn");
        showError(getContextualError(error, "teacher"));
      }
    } finally {
      setLoading("markReturned", false);
    }
  };

  // Filter the live board by the selected class section.
  const classSectionFilteredPasses = selectedClassSectionId
    ? getPassesForClassSection(Number(selectedClassSectionId))
    : passes;

  // Further filter by the selected period, matching against the pass's
  // class section periodName as-is (selectedPeriod is itself a periodName
  // value sourced from classSections, so plain equality is sufficient).
  const filteredPasses = selectedPeriod
    ? classSectionFilteredPasses.filter((pass) => {
        const section = pass.classSectionId
          ? classSectionById.get(pass.classSectionId)
          : undefined;
        return section?.periodName === selectedPeriod;
      })
    : classSectionFilteredPasses;

  const pendingPasses = filteredPasses
    .filter((p) => p.status === PASS_STATUS.PENDING)
    .sort(
      (a, b) =>
        new Date(a.requestedAt).getTime() - new Date(b.requestedAt).getTime(),
    ); // FIFO: oldest first

  const currentlyOutPasses = filteredPasses.filter(
    (p) => p.status === PASS_STATUS.ACTIVE,
  );
  const overLimitPasses = currentlyOutPasses.filter((p) => isOverLimit(p));
  const outPassesInTime = currentlyOutPasses.filter((p) => !isOverLimit(p));

  const waitingPasses = filteredPasses
    .filter((p) => p.status === PASS_STATUS.WAITING)
    .sort(
      (a, b) =>
        new Date(a.approvedAt!).getTime() - new Date(b.approvedAt!).getTime(),
    ); // Sort by approval time (FIFO)

  // Show initial loading screen
  if (isInitialLoading) {
    return <LoadingScreen message="Loading teacher dashboard..." />;
  }

  // Show schedule error screen if there's a configuration issue
  if (scheduleError) {
    return <ScheduleErrorScreen error={scheduleError} />;
  }

  return (
    <div className={styles["dashboard"]}>
      <header>
        <div>
          <h1>
            Teacher Dashboard
            {isDeveloperMode && (
              <span className={styles["dev-mode-badge"]}>🔧 DEV MODE</span>
            )}
          </h1>
          {currentPeriod ? (
            <div className={styles["period-info"]}>
              <span className={styles["current-period"]}>
                {currentPeriod.displayName}
              </span>
              {/* GET /schedule/today carries no abDay, so this is null today;
                  omit the "(_ Day)" suffix entirely rather than rendering a
                  stray "( Day)". */}
              {currentPeriod.abDay && (
                <span className={styles["ab-day"]}>
                  ({currentPeriod.abDay} Day)
                </span>
              )}
              <span className={styles["schedule-type"]}>
                •{" "}
                {scheduleVariantsMap[scheduleType as string] ||
                  getScheduleDisplayName(scheduleType)}
                {/* TODO(slice:foundation): see schoolId comment above */}
              </span>
              {isAutoMode && (
                <span className={styles["time-remaining"]}>
                  • {currentPeriod.timeRemaining} min left
                </span>
              )}
            </div>
          ) : (
            <div className={styles["period-info"]}>
              <span className={styles["no-period"]}>No active period</span>
              {abDay && <span className={styles["ab-day"]}>({abDay} Day)</span>}
              {scheduleType && (
                <span className={styles["schedule-type"]}>
                  •{" "}
                  {scheduleVariantsMap[scheduleType] ||
                    getScheduleDisplayName(scheduleType)}
                  {/* TODO(slice:foundation): see schoolId comment above */}
                </span>
              )}
            </div>
          )}
        </div>
        <div>
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLScdXhb7Qs0IOmSG3UoMLMqOP1CBpoSKFbdAe6iExkyrenIGqg/viewform?usp=header"
            target="_blank"
            rel="noopener noreferrer"
            className={styles["bug-report-link"]}
          >
            🐛 Report Bug
          </a>
          <span>Welcome, {currentUser?.name}</span>
          <button onClick={logout}>Logout</button>
        </div>
      </header>

      <ConnectionStatusBanner connectionStatus={connectionStatus} />

      <div className={styles["teacher-controls"]}>
        <div
          className={styles["action-buttons"]}
          style={{ display: "flex", gap: "10px", marginBottom: "15px" }}
        >
          <button
            className={styles["create-pass-btn"]}
            onClick={() => setShowPassSubmission(true)}
          >
            ➕ Create Pass for Student
          </button>
        </div>

        <div className={styles["control-group"]}>
          <label htmlFor="class-section-select">Class Section:</label>
          <select
            id="class-section-select"
            value={selectedClassSectionId}
            onChange={(e) => {
              setSelectedClassSectionId(e.target.value);
              setHasUserSelectedSection(true);
            }}
            className={styles["class-section-select"]}
          >
            <option value="">All Class Sections</option>
            {classSections.map((cs) => (
              <option key={cs.id} value={cs.id}>
                {cs.periodName} - {cs.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles["control-group"]}>
          <label htmlFor="period-select">Period:</label>
          <select
            id="period-select"
            value={selectedPeriod}
            onChange={(e) => {
              setSelectedPeriod(e.target.value);
              setHasUserSelectedPeriod(true);
            }}
            className={styles["period-select"]}
          >
            <option value="">All periods</option>
            {periodOptions.map((periodName) => (
              <option key={periodName} value={periodName}>
                {periodName}
              </option>
            ))}
          </select>
        </div>

        <div className={styles["control-group"]}>
          <span className={styles["viewing-info"]}>
            Viewing:{" "}
            {selectedClassSectionId
              ? getClassSectionName(Number(selectedClassSectionId))
              : "All Class Sections"}{" "}
            - {selectedPeriod || "All Periods"}
          </span>
        </div>
      </div>

      <main>
        <div className={styles["pass-sections"]}>
          <section className="pending-section">
            <h2>📋 Pending Requests ({pendingPasses.length})</h2>
            {pendingPasses.length === 0 ? (
              <p>No pending requests.</p>
            ) : (
              <div className={styles["pass-list"]}>
                {pendingPasses.map((pass) => (
                  <div key={pass.id} className={styles["pass-card"]}>
                    <div className={styles["pass-info"]}>
                      <strong>{pass.studentName}</strong>
                      <p>{pass.destinationName}</p>
                      <p>
                        <small>
                          from {getClassSectionName(pass.classSectionId)}
                        </small>
                      </p>
                      <small>
                        Requested:{" "}
                        {new Date(pass.requestedAt).toLocaleTimeString()}
                      </small>
                    </div>
                    <div className={styles["pass-actions"]}>
                      <button
                        onClick={() => approvePass(pass.id)}
                        disabled={approvingPassIds.has(pass.id)}
                        className={styles["approve-btn"]}
                      >
                        {approvingPassIds.has(pass.id)
                          ? "Approving..."
                          : "Approve"}
                      </button>
                      <button
                        onClick={() => denyPass(pass.id)}
                        disabled={loadingStates.denyPass}
                        className={styles["deny-btn"]}
                      >
                        {loadingStates.denyPass ? "Denying..." : "Deny"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="approved-section">
            <h2>✅ Approved & Waiting ({waitingPasses.length})</h2>
            {waitingPasses.length === 0 ? (
              <p>No students waiting to leave.</p>
            ) : (
              <div className={styles["pass-list"]}>
                {waitingPasses.map((pass) => (
                  <div
                    key={pass.id}
                    className={`${styles["pass-card"]} ${styles["waiting-pass"]}`}
                  >
                    <div className={styles["pass-info"]}>
                      <strong>{pass.studentName}</strong>
                      <p>{pass.destinationName}</p>
                      <p>
                        <small>
                          from {getClassSectionName(pass.classSectionId)}
                        </small>
                      </p>
                      <small>
                        Approved:{" "}
                        {new Date(pass.approvedAt!).toLocaleTimeString()}
                      </small>
                      <span className={styles["waiting-badge"]}>
                        Destination Full
                      </span>
                    </div>
                    <div className={styles["pass-actions"]}>
                      <button
                        onClick={() => cancelPass(pass.id)}
                        disabled={loadingStates.cancelPass}
                        className={styles["cancel-btn"]}
                      >
                        {loadingStates.cancelPass ? "Canceling..." : "Cancel"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="active-section">
            <h2>🚶 Currently Out ({outPassesInTime.length})</h2>
            {outPassesInTime.length === 0 ? (
              <p>No students currently out from your class.</p>
            ) : (
              <div className={styles["pass-list"]}>
                {outPassesInTime.map((pass) => (
                  <div key={pass.id} className={styles["pass-card"]}>
                    <div className={styles["pass-info"]}>
                      <strong>{pass.studentName}</strong>
                      <p>{pass.destinationName}</p>
                      <p>
                        <small>
                          from {getClassSectionName(pass.classSectionId)}
                        </small>
                      </p>
                      <small>
                        Left: {new Date(pass.activatedAt!).toLocaleTimeString()}
                      </small>
                    </div>
                    <div className={styles["pass-actions"]}>
                      <button
                        onClick={() => markReturned(pass.id)}
                        disabled={loadingStates.markReturned}
                        className={styles["return-btn"]}
                      >
                        {loadingStates.markReturned
                          ? "Marking..."
                          : "Mark Returned"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Over Limit Section - Students out longer than 10 minutes */}
          <section className="over-limit-section">
            <h2 style={{ color: "#856404" }}>
              ⚠️ Over 10 Minute Limit ({overLimitPasses.length})
            </h2>
            {overLimitPasses.length === 0 ? (
              <p>No students currently over the time limit.</p>
            ) : (
              <div className={styles["pass-list"]}>
                {overLimitPasses.map((pass) => (
                  <div
                    key={pass.id}
                    className={styles["pass-card"]}
                    style={{
                      backgroundColor: "#fff3cd",
                      borderColor: "#ffc107",
                    }}
                  >
                    <div className={styles["pass-info"]}>
                      <strong>{pass.studentName}</strong>
                      <p>{pass.destinationName}</p>
                      <p>
                        <small>
                          from {getClassSectionName(pass.classSectionId)}
                        </small>
                      </p>
                      <div
                        style={{
                          backgroundColor: "#ffc107",
                          color: "#856404",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "0.8rem",
                          fontWeight: "bold",
                          display: "inline-block",
                          marginTop: "5px",
                        }}
                      >
                        Over 10 min
                      </div>
                      <small style={{ display: "block", marginTop: "5px" }}>
                        Left: {new Date(pass.activatedAt!).toLocaleTimeString()}
                      </small>
                    </div>
                    <div className={styles["pass-actions"]}>
                      <button
                        onClick={() => markReturned(pass.id)}
                        disabled={loadingStates.markReturned}
                        className={styles["return-btn"]}
                      >
                        {loadingStates.markReturned
                          ? "Marking..."
                          : "Mark Returned"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* TODO: En Route section removed — no destination-to-classSection/teacher mapping exists in hallpass-backend; revisit if the backend adds one */}

        {/* Pass History Analytics Section */}
        <section className={styles["pass-history-section"]}>
          <h2>📊 Pass History & Analytics</h2>
          {/* TODO(slice:foundation): see schoolId comment above */}
          <PassHistoryView orgId={schoolId} />
        </section>
      </main>

      {/* Pass Submission Modal */}
      <TeacherPassSubmission
        isOpen={showPassSubmission}
        onClose={() => setShowPassSubmission(false)}
        destinations={destinations}
        destinationsError={destinationsError}
        onRetryDestinations={fetchDestinations}
      />
    </div>
  );
};

export default TeacherDashboard;
